import { AssetType, OrderActivity, OrderLeg } from '@tradingblock/types';
import _ from 'lodash';
import React, { FC } from 'react';
import { isErrorActivity } from '../../../../utilities/activity';
import { ActivityOrderStatusWrapper } from '../../ActivityOrderStatusWrapper';
import {
  OrderStatusToString,
  isFilledOrderStatus,
  isPartialOrderStatus,
  ActivityStatusMessage,
  isPartiallyCancelledOrderStatus,
} from '../../util';

/**
 * For multileg, order Quantity / FillQuantity are spread counts (see Order.Quantity in types).
 * Do not multiply by per-leg SpreadRatio here — that is only the weight within one spread; Q×R
 * would be option contracts and doubles the spread ratio vs positions / order ticket expectations.
 */
const getLegFilledTotal = (l: OrderLeg, activity: OrderActivity) => {
  const legs = activity.Legs;
  const multileg = legs != null && legs.length > 1;
  if (multileg) {
    const { FillQuantity, OrderStatus, Quantity } = activity;
    if (isFilledOrderStatus(OrderStatus)) {
      return FillQuantity != null ? FillQuantity : Quantity;
    }
    if (isPartialOrderStatus(OrderStatus) || isPartiallyCancelledOrderStatus(OrderStatus)) {
      if (FillQuantity != null) {
        return FillQuantity;
      }
      if (l.LegFillQuantity != null) {
        return Math.round(l.LegFillQuantity / (l.SpreadRatio || 1));
      }
      return 0;
    }
    return 0;
  }
  const orderQuantity = activity.Quantity;
  const orderStatus = activity.OrderStatus;
  const filledStatus = isFilledOrderStatus(orderStatus);
  const { SpreadRatio } = l;
  const singleEquityLeg = legs != null && legs.length === 1 && l.AssetType === AssetType.Equity;
  if (singleEquityLeg) {
    if (filledStatus) {
      if (l.LegFillQuantity != null) {
        return l.LegFillQuantity;
      }
      if (activity.FillQuantity != null) {
        return activity.FillQuantity;
      }
      return orderQuantity;
    }
    return l.LegFillQuantity || 0;
  }
  if (filledStatus) {
    return orderQuantity * (SpreadRatio || 1);
  }
  return l.LegFillQuantity || 0;
};

const OrderLegsStatus: FC<{ activity: OrderActivity }> = ({ activity }) => {
  const { Legs, Quantity } = activity;
  const maxInd = _.size(Legs) - 1;
  const multileg = Legs != null && Legs.length > 1;
  return (
    <>
      {_.map(Legs, (l, ind) => {
        const filledQuantity = getLegFilledTotal(l, activity);
        const singleEquity = !multileg && l.AssetType === AssetType.Equity;
        const totalQuantity = multileg ? Quantity : singleEquity ? Quantity : (l.SpreadRatio || 1) * Quantity;
        return (
          <div key={ind}>
            {`${filledQuantity} / ${totalQuantity}`}
            {maxInd > ind && <br />}
          </div>
        );
      })}
    </>
  );
};

export const OrderActivityStatus: FC<{ activity: OrderActivity }> = ({ activity }) => {
  const { OrderStatus, Legs } = activity;
  const status = OrderStatusToString(OrderStatus);
  const filledStatus = isFilledOrderStatus(OrderStatus);
  const partialStatus = isPartialOrderStatus(OrderStatus);
  const partiallyCancelledStatus = isPartiallyCancelledOrderStatus(OrderStatus);
  let statusClass = undefined;
  const statusMore = ActivityStatusMessage(activity);
  let statusValue = status;
  if (filledStatus) {
    statusClass = 'pos';
  } else if (partialStatus || partiallyCancelledStatus) {
    statusClass = 'pending';
  } else if (isErrorActivity(activity)) {
    statusValue = 'Error';
    statusClass = 'neg';
  }

  const showLegStatus = isErrorActivity(activity) === true ? false : true;
  const showLegMore = showLegStatus || statusMore;
  return (
    <ActivityOrderStatusWrapper status={OrderStatus}>
      <span className={statusClass}>{statusValue}</span>
      {showLegMore && (
        <>
          <br />
          {(filledStatus || partialStatus) && <OrderLegsStatus activity={activity} />}
          {!filledStatus && !partialStatus && statusMore && <>{statusMore}</>}
        </>
      )}

      {/* {statusMore && <>{statusMore}</>} */}
    </ActivityOrderStatusWrapper>
  );
};
